import type { Building } from "building-types/building/buildingTypes";
import type { BuildingAnalyticEntity, BuildingAnalyticEntityStyle } from "building-types/buildingAnalyticEntityTypes";
import {
  buildingGeomAnalyticTagNew,
  buildingGeomAttributeTagNew,
} from "building-types/buildingGeomTag/buildingGeomTagTypes";
import type { BuildingGeomTag } from "building-types/buildingGeomTag/buildingGeomTagTypes";
import type { BuildingEntityHandle } from "building-types/entities/buildingEntityHandle";
import type { FloorFinish } from "building-types/floors/floorFinishTypes";
import type { DocEntityHandle, LevelHandle, RoomHandle } from "building-types/handles/docEntityHandleTypes";
import type { BaseTrimMolding, CrownTrimMolding } from "building-types/molding/trimMoldingTypes";
import type { RoomSet } from "building-types/rooms/roomTraversalTypes";
import type { RWTable, Table } from "common/data/table";
import { materialEntityKey, type MaterialSrc } from "common/materials/materialSrc";
import { percentNew, worldInchesNew } from "common/utils/nominalNumbers";
import { materialSrcFromPalette } from "doc/materialLookup";
import type { Face } from "geom/brep";
import { faceIdsFromPlanarGraphsByPoint } from "geom/faceIdsFromPlanarGraphByPoint";
import type { Line3Segment } from "geom/line3";
import { planeNewXY, planeProjectPolyLine3 } from "geom/plane";
import { polyLine2EdgeCount } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { polyLine3AtZ, polyLine3Edge, polyLine3EdgeCount, polyLine3Length } from "geom/polyLine3";
import type { PolyLine3 } from "geom/polyLine3";
import { splitPolyLine2ByRegions } from "geom/trim";
import { addAnalyticBRep, addAnalyticLineSegment } from "../utils/generateUtil";
import makeBuildingSelector from "../utils/makeBuildingSelector";
import memo from "../utils/memo";

const trimStyle: BuildingAnalyticEntityStyle = {
  color: 0xffffff,
  lineWidth3d: worldInchesNew(3),
  opacity: percentNew(0),
};

const crownTrimMoldingStyle: BuildingAnalyticEntityStyle = {
  color: 0x00ff00,
  lineWidth3d: worldInchesNew(2),
  opacity: percentNew(0.5),
};

const baseTrimMoldingStyle: BuildingAnalyticEntityStyle = {
  color: 0x0000ff,
  lineWidth3d: worldInchesNew(2),
  opacity: percentNew(0.5),
};

export const FLOOR_MATERIAL_ATTRIBUTE = "FLOOR_MATERIAL";
export const INTERIOR_TRIM_LENGTH_ATTRIBUTE = "INTERIOR_TRIM_LENGTH";
export const INTERIOR_TRIM_MATERIAL_ATTRIBUTE = "INTERIOR_TRIM_MATERIAL";

/**
 * Gathers all FloorFinish entries from the building's floor systems
 * for a given level and set of rooms.
 * @todo instead of collecting all level floor finishes, do a spatial query to find the floor finishes that are within the trim's bounding box.
 */
function collectFloorFinishes(building: ReturnType<typeof select>, levelId: string, roomSet: RoomSet): FloorFinish[] {
  const floorSystem = building.floorSystems[levelId];
  if (!floorSystem) {
    return [];
  }

  const finishes: FloorFinish[] = [];
  for (const room of roomSet) {
    const roomFinishes = floorSystem.roomFloorFinishes[room.id];
    if (roomFinishes) {
      for (const finish of roomFinishes) {
        finishes.push(finish);
      }
    }
  }
  return finishes;
}

interface ClassifiedSubSegment {
  readonly rail2D: PolyLine2;
  readonly materialSrc: MaterialSrc | undefined;
}

/**
 * Projects a trim rail to 2D, splits it against floor finish face outlines,
 * and classifies each sub-segment by which finish region contains it.
 */
function splitRailByFloorFinishes(rail: PolyLine3, finishes: FloorFinish[]): ClassifiedSubSegment[] {
  const rail2D = planeProjectPolyLine3(planeNewXY(), rail);

  if (finishes.length === 0 || polyLine2EdgeCount(rail2D) === 0) {
    return [{ rail2D, materialSrc: undefined }];
  }

  const finishFaces: Face[] = finishes.filter((f) => f.finishFace.loops.length > 0).map((f) => f.finishFace);
  const finishBoundaries: PolyLine2[][] = finishFaces.map((f) => f.loops);
  const classified = splitPolyLine2ByRegions(rail2D, finishBoundaries);

  return classified.map(({ component, regionIndices }) => ({
    rail2D: component,
    materialSrc: regionIndices.length > 0 ? finishes[regionIndices[0]]?.materialSrc : undefined,
  }));
}

function addTrimRailEdges(
  analyticEntities: RWTable<BuildingAnalyticEntity>,
  building: ReturnType<typeof select>,
  trim: BaseTrimMolding | CrownTrimMolding,
  trimHandle: DocEntityHandle,
  levelHandle: LevelHandle,
  rail: PolyLine3,
  tags: BuildingGeomTag[],
  style: BuildingAnalyticEntityStyle,
): void {
  for (let i = 0; i < polyLine3EdgeCount(rail); i++) {
    const lineSegment = polyLine3Edge(rail, i) as unknown as Line3Segment;
    const midpoint = [(lineSegment[0] + lineSegment[3]) / 2, (lineSegment[1] + lineSegment[4]) / 2] as [number, number];
    const roomId = faceIdsFromPlanarGraphsByPoint(
      midpoint,
      building.planarGraphs,
      building.roomsByLevel,
      building.roomLevelIndices,
      [trim.levelId],
    )[0];
    const roomHandle: RoomHandle | undefined = roomId ? { type: "ROOM", id: roomId } : undefined;
    const parents: DocEntityHandle[] = roomHandle ? [trimHandle, levelHandle, roomHandle] : [trimHandle, levelHandle];

    addAnalyticLineSegment(
      "ANALYTIC_INTERIOR_TRIM",
      analyticEntities,
      `${trim.subType} trim`,
      parents,
      lineSegment,
      tags,
      style,
    );
  }
}

export function addTrimAnalyticEntities(
  building: ReturnType<typeof select>,
  buildingEntityHandle: BuildingEntityHandle,
): Table<BuildingAnalyticEntity> {
  const analyticEntities: Table<BuildingAnalyticEntity> = {};

  const trim = (building.baseTrimMoldings[buildingEntityHandle.id] ??
    building.crownTrimMoldings[buildingEntityHandle.id]) as BaseTrimMolding | CrownTrimMolding | null;
  if (!trim) {
    return {};
  }

  const handle: DocEntityHandle = {
    type: "GENERATED_ENTITY",
    entity: buildingEntityHandle,
  };

  const condition = trim.subType;
  const conditionTag = buildingGeomAnalyticTagNew(condition);
  const levelHandle: LevelHandle = { type: "LEVEL", id: trim.levelId };
  const material = materialSrcFromPalette(building.interiorPalette, trim.material);
  const commonTags: BuildingGeomTag[] = [conditionTag];
  if (material) {
    commonTags.push(
      buildingGeomAttributeTagNew(INTERIOR_TRIM_MATERIAL_ATTRIBUTE, materialEntityKey(material), "STRING"),
    );
  }

  const isBaseTrim = condition === "BASE";
  const finishes = isBaseTrim ? collectFloorFinishes(building, trim.levelId, trim.roomSet) : [];

  for (let i = 0; i < trim.trimRails.length; i++) {
    const tt = trim.trims[i];
    const rail = trim.trimRails[i];

    addAnalyticBRep(
      "ANALYTIC_INTERIOR_TRIM",
      analyticEntities,
      condition + " trim",
      [handle, levelHandle],
      tt,
      undefined,
      undefined,
      undefined,
      [
        ...commonTags,
        buildingGeomAttributeTagNew(INTERIOR_TRIM_LENGTH_ATTRIBUTE, worldInchesNew(polyLine3Length(rail)), "INCHES"),
      ],
      trimStyle,
    );

    if (isBaseTrim) {
      const classifiedSegments = splitRailByFloorFinishes(rail, finishes);
      const z = rail[2];
      for (const { rail2D, materialSrc } of classifiedSegments) {
        const subRail = polyLine3AtZ(rail2D, z);
        const tags: BuildingGeomTag[] = [...commonTags];
        if (materialSrc) {
          tags.push(buildingGeomAttributeTagNew(FLOOR_MATERIAL_ATTRIBUTE, materialEntityKey(materialSrc), "STRING"));
        }
        addTrimRailEdges(analyticEntities, building, trim, handle, levelHandle, subRail, tags, baseTrimMoldingStyle);
      }
    } else {
      addTrimRailEdges(analyticEntities, building, trim, handle, levelHandle, rail, commonTags, crownTrimMoldingStyle);
    }
  }

  return analyticEntities;
}

const select = makeBuildingSelector([
  "baseTrimMoldings",
  "crownTrimMoldings",
  "interiorPalette",
  "floorSystems",
  "planarGraphs",
  "roomsByLevel",
  "roomLevelIndices",
]);

function generate(building: ReturnType<typeof select>): Pick<Building, "analyticInteriorTrimEntities"> {
  const analyticInteriorTrimEntities: RWTable<BuildingAnalyticEntity> = {};
  for (const id of new Set([...Object.keys(building.baseTrimMoldings), ...Object.keys(building.crownTrimMoldings)])) {
    Object.assign(analyticInteriorTrimEntities, addTrimAnalyticEntities(building, { entityType: "INTERIOR_TRIM", id }));
  }
  return { analyticInteriorTrimEntities };
}

export default memo(select, generate);
